import { useState, useMemo } from "react";
import BigNumber from "bignumber.js";
import type {
  RetirementData,
  RetirementResult,
} from "../store/retirementStore";
import type { AssetBreakdown } from "../types/projection";
import { buildProjectionRows } from "../utils/projectionRows";
import { cn } from "../utils/cn";
import { ProjectionTable } from "./ProjectionTable";
import { ProjectionCard } from "./ProjectionCard";
import { IncomeAnalysis } from "./IncomeAnalysis";
import { AssumptionsDisplay } from "./AssumptionsDisplay";

type DetailTab = "table" | "income" | "assumptions";

const TABS: { id: DetailTab; label: string }[] = [
  { id: "table", label: "📊 Table" },
  { id: "income", label: "💰 Income" },
  { id: "assumptions", label: "⚙️ Assumptions" },
];

interface ProjectionDetailPageProps {
  isOpen: boolean;
  data: RetirementData;
  result: RetirementResult;
  assetBreakdown?: AssetBreakdown;
  onClose: () => void;
  /** Called when an [Edit →] link needs to navigate to settings */
  onOpenSettings?: (category: string) => void;
  /** Whether to show mobile card view (defaults to false) */
  useMobileCards?: boolean;
}

export function ProjectionDetailPage({
  isOpen,
  data,
  result,
  assetBreakdown,
  onClose,
  onOpenSettings,
  useMobileCards = false,
}: ProjectionDetailPageProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>("table");
  const [cardIndex, setCardIndex] = useState(0);

  const rows = useMemo(
    () => buildProjectionRows(data, assetBreakdown),
    [data, assetBreakdown]
  );

  const sustainableMonthlyIncome = useMemo(
    () =>
      new BigNumber(result.finalAmount)
        .times(data.annualReturn)
        .div(12)
        .toNumber(),
    [result.finalAmount, data.annualReturn]
  );

  const retirementMonthlyExpenses = useMemo(
    () =>
      new BigNumber(data.monthlyExpenses)
        .times(1 + data.expensesAtRetirement)
        .toNumber(),
    [data.monthlyExpenses, data.expensesAtRetirement]
  );

  const retirementAge = Math.round(data.retirementAge);
  const currentYear = new Date().getFullYear();
  const retirementYear =
    currentYear + Math.round(data.retirementAge - data.currentAge);

  if (!isOpen) return null;

  return (
    <div className="projection-detail-overlay" id="projection-detail-page">
      <div className="projection-detail-content">
        {/* Header */}
        <div className="projection-detail-header">
          <button
            type="button"
            onClick={onClose}
            className="projection-detail-back-btn"
            aria-label="Close projection details"
          >
            ←
          </button>
          <h2 className="projection-detail-title">Projection Details</h2>
        </div>

        {/* Tab navigation */}
        <div className="projection-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={cn(
                "projection-modal-tab",
                activeTab === tab.id && "projection-modal-tab--active"
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="projection-detail-body">
          {activeTab === "table" && (
            <div className="projection-sections">
              {/* Legend for phase badges */}
              <div className="phase-info-row">
                <span className="phase-badge-wrapper">
                  <span className="phase-badge-working-combined">W</span>{" "}
                  Working
                </span>
                <span className="phase-badge-wrapper">
                  <span className="phase-badge-retired-combined">R</span>{" "}
                  Retired
                </span>
              </div>

              {useMobileCards ? (
                <ProjectionCard
                  rows={rows}
                  currentIndex={cardIndex}
                  onNavigate={setCardIndex}
                  currency={data.currency}
                />
              ) : (
                <section>
                  <ProjectionTable rows={rows} currency={data.currency} />
                </section>
              )}
            </div>
          )}

          {activeTab === "income" && (
            <IncomeAnalysis
              cppAnnual={0}
              oasAnnual={0}
              investmentIncomeAnnual={Math.round(sustainableMonthlyIncome * 12)}
              monthlyExpenses={retirementMonthlyExpenses}
              retirementYear={retirementYear}
              retirementAge={retirementAge}
              currency={data.currency}
              onAdjustExpenses={() => onOpenSettings?.("expenses")}
            />
          )}

          {activeTab === "assumptions" && (
            <AssumptionsDisplay
              expectedReturnRate={data.annualReturn}
              inflationRate={data.inflationRate}
              realEstateAppreciationRate={
                assetBreakdown?.realEstateAppreciationRate ?? 0.035
              }
              expenseGrowthRate={Math.abs(data.expensesAtRetirement)}
              onEdit={(category) => onOpenSettings?.(category)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
